/**
 * Theme centralizado para payments-ui (web).
 * Duplica valores de carrefour-ui sin dependencias de react-native.
 * Punto de entrada única para todos los tokens de diseño.
 */

// Paleta completa de carrefour-ui
const palette = {
  neutral100: '#FFFFFF',
  neutral150: '#F2F2F2',
  neutral200: '#F4F2F1',
  neutral300: '#D7CEC9',
  neutral400: '#B6ACA6',
  neutral500: '#978F8A',
  neutral600: '#564E4A',
  neutral650: '#545C66',
  neutral700: '#3C3836',
  neutral800: '#191015',
  neutral850: 'rgba(0, 0, 0, 0.8)',
  neutral900: '#000000',
  neutral1000: '#f0f0f0',
  neutral1100: '#f5f5f5',
  /** Figma Content/Neutral/Pressed. Texto del placeholder de la tarjeta vacía. */
  neutralPressed: '#5E5E5E',

  primary100: '#F4E0D9',
  primary200: '#E8C1B4',
  primary300: '#DDA28E',
  primary400: '#D28468',
  primary500: '#C76542',
  primary600: '#A54F31',

  secondary100: '#DCDDE9',
  secondary200: '#BCC0D6',
  secondary300: '#9196B9',
  secondary400: '#626894',
  secondary500: '#41476E',

  accent100: '#FFEED4',
  accent200: '#FFE1B2',
  accent300: '#FDD495',
  accent400: '#FBC878',
  accent500: '#FFBB50',

  angry100: '#F2D6CD',
  angry500: '#C03403',

  overlay10: 'rgba(25, 16, 21, 0.1)',
  overlay20: 'rgba(25, 16, 21, 0.2)',
  overlay30: 'rgba(25, 16, 21, 0.35)',
  overlay50: 'rgba(25, 16, 21, 0.5)',

  overlayLight25: 'rgba(255, 255, 255, 0.25)',
  overlayLight75: 'rgba(255, 255, 255, 0.75)',

  // CRF palette
  lightBlue100: '#F5FAFF',
  lightBlue200: '#EDF1F5',
  lightBlue250: '#EDFAFF',
  lightBlue275: '#E8F8FF',
  lightBlue300: '#DEEDFC',
  lightBlue400: '#BDD8F1',
  lightBlue450: '#B9DDFF',
  lightBlue500: '#58b2ff',

  blue100: '#0970E6',
  blue150: '#003569',
  blue160: '#0970E660',
  blue200: '#0757B2',
  blue300: '#004F9B',
  blue350: '#005BAA',
  blue400: '#173EB4',
  blue500: '#064C9C',
  blue600: '#447CBB',
  blue700: '#447AB8',
  blue800: '#1A4587',

  darkBlue: '#292770',
  darkBlue100: '#004E9B',

  grey50: '#FAFAFA',
  grey100: '#F7F7F7',
  grey150: '#EBEBEB',
  grey200: '#E5E5E5',
  grey300: '#D9D9D9',
  grey350: '#D9D9D9',
  grey400: '#C9C9C9',
  grey500: '#AFAFAF',
  grey600: '#929292',
  grey625: '#888888',
  grey650: '#878787',
  grey700: '#747474',
  grey750: '#696969',
  grey800: '#575757',
  grey900: '#454545',

  red50: '#F6EDFF',
  red100: '#D30D1F',
  red200: '#E51E26',
  red300: '#AA0D11',

  lightRed100: '#FFEFEF',

  green100: '#33691E',
  green150: '#2C815E',
  green200: '#2DBD7B',

  lightGreen100: '#E9FFF2',

  yellow100: '#FFB300',

  coral100: '#eb3817',
  lightCoral100: '#FFF4F2',

  cyan100: '#0887b2',
  lightCyan100: '#EDFDFF',

  amber100: '#d99106',
  lightAmber100: '#FFFEF2',

  oceanBlue100: '#0270c7',

  darkGrey100: '#262626',
  darkGrey200: '#121212',

  orange100: '#FF6B00',

  purple80: '#0C0020',
  purple85: '#1F1044',
  purple90: '#410092',
  purple100: '#3400A4',
  purple200: '#69498c',
  purple300: '#723DAC',
  purple350: '#6C4A98',
  purple400: '#492B92',
  purple500: '#51386B',
  purple550: '#8B64B9',
  purple600: '#8133ea',

  installmentBadgeBackground: '#2B7D5B',
  installmentBadgeText: '#FFFFFF',

  safeBackground: '#E1FFCF',
  safeText: '#408E10',
  warningBackground: '#FFF4DE',
  warningText: '#FF7A00',
  dangerBackground: '#FFE8E8',
  dangerText: '#ED1C24',
};

// Spacing (de carrefour-ui)
const spacing = {
  micro: 2,
  tiny: 4,
  extraSmall: 8,
  mediumSmall: 10,
  small: 12,
  smallLarge: 14,
  medium: 16,
  mediumXl: 18,
  mediumLarge: 20,
  mediumLargeXl: 22,
  large: 24,
  largeXl: 28,
  extraLarge: 32,
  extraLargeXl: 36,
  semiHuge: 40,
  semiHugeXl: 44,
  huge: 48,
  hugeXl: 50,
  hugeXxl: 52,
  massiveSmall: 60,
  massive: 64,
  extraMassive: 70,
  xxextraMassive: 76,
  massiveXl: 80,
  largest: 82,
  xxlarge: 90,
  giant: 100,
  giantSmall: 110,
  giantxl: 120,
  giantLarge: 130,
  giantXxL: 140,
  colossalSmall: 150,
  colossal: 160,
  gigantic: 180,
  enormous: 200,
  enormousSmall: 220,
  enormousMedium: 240,
  enormousLarge: 260,
  enormousXl: 280,
  monster: 300,
};

export const theme = {
  colors: {
    palette,
    white: palette.neutral100,
    black: palette.neutral900,
    transparent: 'rgba(0, 0, 0, 0)',
    text: palette.grey700,
    textDim: palette.neutral600,
    background: palette.neutral100,
    border: palette.neutral400,
    tint: palette.primary500,
    separator: palette.neutral300,
    error: palette.red300,
    errorBackground: palette.angry100,

    // Payments-UI specific colors
    label: palette.grey900, // #454545
    subtitle: palette.grey800, // #575757
    placeholder: palette.grey500, // #AFAFAF
    inputBg: palette.neutral100,
    inputBorder: palette.grey300,
    inputText: palette.grey700,
    button: {
      primary: palette.blue100,
      primaryHover: palette.blue200,
      primaryActive: palette.blue300,
    },
    card: {
      light: palette.lightBlue100,
      border: palette.grey300,
      borderDashed: palette.grey300,
    },
    mercadopago: {
      background: palette.lightBlue250,
      border: palette.grey200,
      icon: palette.blue100,
    },
    success: palette.green200,
    warning: palette.amber100,
    danger: palette.red100,
    installmentBadge: {
      background: palette.installmentBadgeBackground,
      text: palette.installmentBadgeText,
    },
  },

  spacing,

  typography: {
    fontFamily: "'Ubuntu', sans-serif",
    cardFontFamily: "'Sometype Mono', ui-monospace, monospace",
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 20,
      xl: 24,
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      bold: 700,
    },
    lineHeight: {
      xs: '14px',
      sm: '16px',
      base: '20px',
      lg: '24px',
      xl: '28px',
    },
  },

  radius: {
    none: 0,
    sm: 4,
    md: 8,
  },

  shadows: {
    sm: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    md: '0px 4px 12px rgba(0, 0, 0, 0.15)',
  },

  transitions: {
    fast: '0.2s ease-in-out',
    normal: '0.3s ease-in-out',
    slow: '0.5s ease-in-out',
  },

  // Component-specific sizes (only values not in spacing scale)
  sizes: {
    // Small dots (not in spacing)
    dot: {
      width: 5,
      height: 5,
    },
    // Payment method cards - minHeight not in spacing
    paymentCard: {
      minHeight: 184,
      minWidth: spacing.enormousXl, // 280
      // Mobile (medidas del diseño Figma): tarjeta enfocada 2:1; no enfocada ~78% de su alto (misma centro vertical).
      aspectRatio: '2 / 1',
      aspectRatioUnfocused: '2.56 / 1',
    },
    // Text widths - only small (145) not in spacing, medium uses spacing.gigantic
    textWidth: {
      small: 145,
      medium: spacing.gigantic, // 180
    },
  },
} as const;

export type Theme = typeof theme;
